import React, { useEffect, useRef, useState } from 'react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import ReactSelect from 'react-select';
import { Button, Card, CardBody, CardHeader, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, Select, SelectItem, useDisclosure } from '@nextui-org/react'
import ControlledInput from '../../../../elements/ControlledInput';
import toast from 'react-hot-toast';
import { PlusIcon } from '@heroicons/react/24/outline';
import { isResponseErrorObject, isiKolomDariPerusahaan } from '../../../../../services/helpers';
import useSWR from 'swr';
import { getFetcher, postFetcher } from '../../../../../services/api';
import ControlledReactSelect from '../../../../elements/ControlledReactSelect';
import { yupResolver } from '@hookform/resolvers/yup';
import { inlineAsalMuatValidation } from '../../../../../services/validation/asal-muat.validation';

export default function InlineEditAsalMuat({ parentIndex, companyId, control, setValue, resetField }) {
    const [filteredDetail, setFilteredDetail] = useState({});
    const {
        isOpen: isOpenModalForm, 
        onOpen: onOpenModalForm, 
        onOpenChange: onOpenChangeModalForm, 
        onClose: onCloseModalForm
    } = useDisclosure();
    const watchedField = useWatch({
        control,
        name: `perusahaanAsalMuatDanTujuanBongkar[${parentIndex}].perusahaanAsalMuat.perusahaanAsalMuatId`
    })
    
    const { 
        register: registerInline,
        control: controlInline, 
        setValue: setValueInline,
        handleSubmit: handleSubmitInline, 
        reset: resetInline, 
        watch: watchInline,
        formState: {  errors: errorsInline, isSubmitting: isSubmittingInline } 
    } = useForm({ resolver: yupResolver(inlineAsalMuatValidation) });
    const { 
        data: dataAsalMuat, 
        isLoading: isLoadingAsalMuat, 
        mutate: mutateAsalMuat 
    } = useSWR(companyId ? `/api/company/search-perusahaan-asal-muat?companyId=${companyId}` : null, getFetcher);
    const provinceId = watchInline('provinceId')
    const regencyId = watchInline('regencyId')
    const districtId = watchInline('districtId')

    // Select lokasi NextUI dipasangi register() biasa, jadi nilainya TIDAK ikut
    // berubah saat diisi lewat setValue - tampilannya tetap "Pilih..." padahal
    // nilainya sudah ada di form. selectedKeys dijahit ke nilai form supaya
    // pengisian otomatis dari data perusahaan benar-benar terlihat.
    const nilaiFormInline = useWatch({ control: controlInline });
    /*
     * Daftar pilihan kolom "Perusahaan" pada modal tambah asal muat.
     *
     * Bukan isian bebas dan bukan seluruh perusahaan: hanya yang sudah tertaut
     * SIMPEL (idCompanySimpel terisi) DAN bertipe Impor, Produksi, atau
     * Distribusi B3. Penyaringan dikerjakan backend lewat search-company supaya
     * 8.585 baris perusahaan tidak perlu diunduh semuanya.
     *
     * Komponen ini dirender satu kali per baris bahan B3, tapi kunci SWR-nya
     * sama sehingga tetap satu permintaan.
     */
    const TIPE_ASAL_MUAT = ['Impor B3', 'Produksi B3', 'Distribusi B3'].join(',')
    const { data: dataPerusahaanTerdaftar, isLoading: isLoadingPerusahaanTerdaftar } = useSWR(
        `/api/company/search-company?returnAll=true&hasIdCompanySimpel=true&tipePerusahaan=${encodeURIComponent(TIPE_ASAL_MUAT)}`,
        getFetcher
    )

    // Yang disimpan adalah nama (teks), dan ada perusahaan yang namanya sama
    // persis - didedup supaya tidak muncul opsi kembar yang tak terbedakan.
    const opsiPerusahaanTerdaftar = [
        ...new Map(
            (dataPerusahaanTerdaftar?.data ?? [])
                .filter((item) => item?.name)
                .map((item) => [item.name.trim(), { label: item.name.trim(), value: item.name.trim(), perusahaan: item }])
        ).values(),
    ].sort((a, b) => a.label.localeCompare(b.label, 'id'))

    const { data: dataProvince } = useSWR('/api/location/provinces', getFetcher)
    const { data: dataRegency } = useSWR(provinceId ? `/api/location/cities?provinceId=${provinceId}`: null, getFetcher)
    const { data: dataDistrict } = useSWR(regencyId ? `/api/location/districts?regencyId=${regencyId}`: null, getFetcher)
    const { data: dataVillage } = useSWR(districtId ? `/api/location/villages?districtId=${districtId}`: null, getFetcher)

    useEffect(() => {
        if (dataAsalMuat && watchedField) {
            const detail = dataAsalMuat?.data?.find((item) => item.id === watchedField)
            const updatedDetails = {
                perusahaanAsalMuatId: detail?.id,
                namaPerusahaan: detail?.namaPerusahaan,
                locationType: detail?.locationType,
                longitudeAsalMuat: detail?.longitude,
                latitudeAsalMuat: detail?.latitude,
            }
            setFilteredDetail(updatedDetails);
            setValue(`perusahaanAsalMuatDanTujuanBongkar[${parentIndex}].perusahaanAsalMuat`, updatedDetails);
        }
    
    }, [watchedField])
    
    
    function onCloseForm() {
        resetInline({
            namaPerusahaan: '',
            alamat: '',
            locationType: '',
            latitude: '',
            longitude: '',
            provinceId: '',
            regencyId: '',
            districtId: '',
            villageId: '',
        });
        onCloseModalForm()
    }

    async function onSubmitForm(formData) {
        try {
            formData.companyId = companyId
            const response =  await postFetcher('/api/company/asal-muat', formData);
            mutateAsalMuat()
            setValue(`perusahaanAsalMuatDanTujuanBongkar[${parentIndex}].perusahaanAsalMuat.perusahaanAsalMuatId`, response.id)
            toast.success('Perusahaan asal muat berhasil ditambah!');
            onCloseForm();
        } catch (error) {
            console.log(error);
            
            isResponseErrorObject(error.response.data.message)
                ? Object.entries(error.response.data.message).forEach(([key, value]) => {
                    toast.error(value);
                })
                : toast.error(error.response.data.message)
        }
    }
    
    return (
        <Card radius='sm'>
            <CardHeader className='w-full flex justify-between'>
                <label className='text-sm mb-2'>Perusahaan Asal Muat <span className='text-danger'>*</span></label>
                <Button onPress={onOpenModalForm} color='primary' isIconOnly><PlusIcon className='size-5'/></Button>
            </CardHeader>
            <CardBody>
                <div className='flex items-end gap-3'>
                    <div className='flex-1'>
                        <ControlledReactSelect 
                            control={control} 
                            isLoading={isLoadingAsalMuat} 
                            label=""
                            name={`perusahaanAsalMuatDanTujuanBongkar[${parentIndex}].perusahaanAsalMuat.perusahaanAsalMuatId`}
                            options={dataAsalMuat?.data?.map((item) => ({value: item.id, label: item.namaPerusahaan}))}
                        />
                    </div>
                </div>
                <div className="mt-6">
                    <div className='grid grid-cols-2 items-end'>
                        <div className='text-sm text-ink-500 uppercase'>Nama Perusahaan:</div>
                        <div className='text-sm font-medium'>{filteredDetail?.namaPerusahaan }</div>
                        <div className='text-sm text-ink-500 uppercase'>Tipe Lokasi:</div>
                        <div className='text-sm font-medium'>{filteredDetail?.locationType }</div>
                        <div className='text-sm text-ink-500 uppercase'>Longitude:</div>
                        <div className='text-sm font-medium'>{filteredDetail?.longitudeAsalMuat}</div>
                        <div className='text-sm text-ink-500 uppercase'>Latitude:</div>
                        <div className='text-sm font-medium'>{filteredDetail?.latitudeAsalMuat }</div>
                    </div>
                </div>
            </CardBody>

            <Modal size="3xl" isOpen={isOpenModalForm} scrollBehavior='inside' onOpenChange={onOpenChangeModalForm} onClose={onCloseForm} isDismissable={false} isKeyboardDismissDisabled={false}>
                <ModalContent>
                    {(onClose) => (
                        <>
                            <ModalHeader>Tambah Perusahaan Asal Muat</ModalHeader>
                            <ModalBody>
                                    <div className='grid grid-cols-1 md:grid-cols-2 gap-3 mb-6'>  
                                        {/* Bukan isian bebas: hanya perusahaan terdaftar SIMPEL
                                            bertipe Impor / Produksi / Distribusi B3. */}
                                        <ControlledReactSelect
                                            control={controlInline}
                                            label="Perusahaan"
                                            name="namaPerusahaan"
                                            options={opsiPerusahaanTerdaftar}
                                            onOptionChange={(opsi) => isiKolomDariPerusahaan(setValueInline, opsi?.perusahaan)}
                                            isLoading={isLoadingPerusahaanTerdaftar}
                                        />
                                        <ControlledInput label="Alamat" name="alamat" type="text" isRequired={true} control={controlInline} />
                                        <Select
                                            {...registerInline('locationType')}
                                            isRequired
                                            variant='faded'
                                            label="Tipe"
                                            labelPlacement="outside"
                                            placeholder='Pilih...'
                                            color={errorsInline.locationType ? 'danger' : 'default'}
                                            isInvalid={errorsInline.locationType}
                                            errorMessage={errorsInline.locationType && errorsInline.locationType.message}
                                        >
                                            <SelectItem key="Factory">Factory</SelectItem>
                                            <SelectItem key="Gudang">Gudang</SelectItem>
                                            <SelectItem key="Pelabuhan">Pelabuhan</SelectItem>
                                        </Select>
                                        <ControlledInput label="Latitude" name="latitude" type="text" isRequired={true} control={controlInline} />
                                        <ControlledInput label="Longitude" name="longitude" type="text" isRequired={true} control={controlInline} />
                                        <Select
                                            {...registerInline('provinceId')}
                                            selectedKeys={nilaiFormInline?.provinceId ? [String(nilaiFormInline?.provinceId)] : []}
                                            isRequired
                                            disallowEmptySelection
                                            variant="faded"
                                            label="Provinsi"
                                            labelPlacement="outside"
                                            placeholder='Pilih...'
                                            color={errorsInline.provinceId ? 'danger' : 'default'}
                                            isInvalid={errorsInline.provinceId}
                                            errorMessage={errorsInline.provinceId && errorsInline.provinceId.message}
                                        >
                                            {dataProvince?.map(item => (
                                                <SelectItem key={item.id} value={item.id}>{item.name}</SelectItem>
                                            ))}
                                        </Select>                    
                                                    <Select
                                                        {...registerInline('regencyId')}
                                                        selectedKeys={nilaiFormInline?.regencyId ? [String(nilaiFormInline?.regencyId)] : []}
                                                        isRequired
                                                        disallowEmptySelection
                                                        variant="faded"
                                                        label="Kabupaten"
                                                        labelPlacement="outside"
                                                        placeholder='Pilih...'
                                                        color={errorsInline.regencyId ? 'danger' : 'default'}
                                                        isInvalid={errorsInline.regencyId}
                                                        errorMessage={errorsInline.regencyId && errorsInline.regencyId.message}
                                                    >
                                                        {dataRegency?.map(item => (
                                                            <SelectItem key={item.id}>{item.name}</SelectItem>
                                                        ))}
                                                    </Select>                    
                                                    <Select
                                                        {...registerInline('districtId')}
                                                        selectedKeys={nilaiFormInline?.districtId ? [String(nilaiFormInline?.districtId)] : []}
                                                        isRequired
                                                        disallowEmptySelection
                                                        variant="faded"
                                                        label="Kecamatan"
                                                        labelPlacement="outside"
                                                        placeholder='Pilih...'
                                                        color={errorsInline.districtId ? 'danger' : 'default'}
                                                        isInvalid={errorsInline.districtId}
                                                        errorMessage={errorsInline.districtId && errorsInline.districtId.message}
                                                    >
                                                        {dataDistrict?.map(item => (
                                                            <SelectItem key={item.id}>{item.name}</SelectItem>
                                                        ))}
                                                    </Select>                    
                                                    <Select
                                                        {...registerInline('villageId')}
                                                        selectedKeys={nilaiFormInline?.villageId ? [String(nilaiFormInline?.villageId)] : []}
                                                        isRequired
                                                        disallowEmptySelection
                                                        variant="faded"
                                                        label="Kelurahan"
                                                        labelPlacement="outside"
                                                        placeholder='Pilih...'
                                                        color={errorsInline.villageId ? 'danger' : 'default'}
                                                        isInvalid={errorsInline.villageId}
                                                        errorMessage={errorsInline.villageId && errorsInline.villageId.message}
                                                    >
                                                        {dataVillage?.map(item => (
                                                            <SelectItem key={item.id}>{item.name}</SelectItem>
                                                        ))}
                                                    </Select> 
                                    </div>
                                    <div className='flex items-center gap-1'>
                                        <Button onPress={handleSubmitInline(onSubmitForm)} isLoading={isSubmittingInline} isDisabled={isSubmittingInline} type='submit' color='primary'>Tambah</Button>
                                        <Button isDisabled={isSubmittingInline} color='danger' variant='faded' onPress={onClose}>Batal</Button>
                                    </div>
                            </ModalBody>
                            <ModalFooter>

                            </ModalFooter>
                        </>
                    )}
                </ModalContent>
            </Modal>
        </Card>
    )
}